import React from "react"
import { cva, VariantProps } from "class-variance-authority"

import { IKTRichText } from "@/types/kontent.types"
import { cn } from "@/lib/utils"
import { Link, RichTextSync, TLinkProps } from "@/components/ui/atoms"
import { Picture } from "@/components/ui/molecules"

const cardVariants = cva("card w-full", {
  variants: {
    variant: {
      primary: "bg-secondary-500 text-neutral-100",
      neutral: "bg-neutral-100 text-neutral-500",
    },
  },
})

type TCardProps = VariantProps<typeof cardVariants> & {
  src?: string
  alt?: string
  title: string
  body?: IKTRichText
  className?: string
  imagePosition?: string
  clamp?: boolean
  isInArticle?: boolean
  link?: string
  linkText?: string
  storybookButton?: TLinkProps
  tagLabel?: string
}

const Card = ({
  title,
  body,
  src,
  alt,
  link,
  linkText,
  isInArticle = false,
  variant,
  tagLabel,
  clamp = true,
  className,
  storybookButton,
}: TCardProps) => {
  return (
    <article
      className={cn(
        "card-base flex",
        className,
        isInArticle ? "flex-col xl:flex-row" : "flex-col"
      )}
    >
      {src ? (
        <Picture
          src={src}
          alt={alt ? alt : ""}
          tagLabel={isInArticle ? undefined : tagLabel}
          variant={isInArticle ? "articleCard" : "vertical"}
          className={cn("shrink-0")}
        />
      ) : null}
      <div className="size-full">
        <div
          className={cn(
            cardVariants({ variant }),
            "h-full",
            isInArticle ? "min-h-[248px]" : "",
            src ? "rounded-b-100" : "rounded-100"
          )}
        >
          <header
            className={cn(
              "flex items-baseline",
              isInArticle
                ? "flex-col-reverse 2xl:flex-row 2xl:justify-between"
                : ""
            )}
          >
            <h3
              className={cn(
                "heading-s w-full pb-7 leading-[125%]",
                variant === "primary" ? "text-neutral-100" : ""
              )}
            >
              {title}
            </h3>
            {isInArticle && tagLabel ? (
              <span className="label-s mb-6 block w-full grow-0 text-right font-500 text-neutral-400 2xl:mb-0 2xl:inline">
                {tagLabel}
              </span>
            ) : null}
          </header>
          <span className={cn(clamp ? "line-clamp-3" : "", "text-neutral-400")}>
            {body ? (
              <RichTextSync
                blockTitle={title}
                rightAlignButtons={true}
                body={body}
              />
            ) : null}
            {storybookButton ? (
              <span className="flex justify-end">
                <Link
                  chapters={[]}
                  variant={storybookButton.variant}
                  className="pb-0 pr-0"
                  iconRight={storybookButton.iconRight}
                  iconLeft={storybookButton.iconLeft}
                  href={storybookButton.href}
                >
                  {storybookButton.children}
                </Link>
              </span>
            ) : null}
          </span>
          {link ? (
            <Link
              chapters={[title, linkText]}
              title={title}
              variant="ghost"
              className="pb-0 pl-0"
              href={link}
            >
              {linkText}
            </Link>
          ) : null}
        </div>
      </div>
    </article>
  )
}

export { Card, type TCardProps, cardVariants }
